/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { all, takeEvery } from "redux-saga/effects";

import {
  APPLICATION_LOAD,
  APPLICATIONS_SEARCH,
  ASSIGN_APPLICATION,
  BULK_ASSIGN_APPLICATION,
  CAPACITY_DATA_LOAD,
  COUNTRYDATA_LOAD,
  CREATE_USER,
  DEMOGRAPHICS_FILTER,
  DOWNLOAD_APPLICATIONS_TO_CSV,
  EVENT_LOAD,
  EVENTS_SEARCH,
  FLAG_APPLICATION,
  GET_ASSIGN_APPLICATION,
  GET_AVAILABLE_ASSIGN_APPLICATION,
  PROGRAM_LANGUAGE_LOAD,
  REGIONDATA_LOAD,
  SCHOOLDATA_LOAD,
  SCORE_APPLICATION,
  SOCIALNETWORKDATA_LOAD,
  STATEPROVINCEDATA_LOAD,
  STATEPROVINCESCHOOL_LOAD,
  STATEPROVINCESHIPPING_LOAD,
  UPDATE_EVENT_STATUS,
  UPDATE_USER_STATUS,
  USER_ROLE_CREATE,
  USER_ROLE_DELETE,
  USER_ROLE_UPDATE,
  USER_ROLES_LOAD,
  USERPROFILE_ADD_COMMENT,
  USERPROFILE_CLAIM_BENEFIT,
  USERPROFILE_DL_LOAD,
  USERPROFILE_DL_REMOVE,
  USERPROFILE_DL_REMOVE_ALL,
  USERPROFILE_END_VSBENEFITS,
  USERPROFILE_EXTEND_VSBENEFITS,
  USERPROFILE_FETCH_PROGRAM_PROFILE,
  USERPROFILE_GET_PROFILE_BENEFITS,
  USERPROFILE_LOAD,
  USERPROFILE_LOAD_BY_EMAIL,
  USERPROFILE_LOAD_COMMENTS,
  USERPROFILE_LOAD_VSBENEFITS,
  USERPROFILE_PROVISION_VSBENEFITS,
  USERPROFILE_UPDATE,
  USERPROFILE_UPDATE_BLOCK_SHIPPING,
  USERPROFILE_UPDATE_COMMENT,
  USERPROFILE_UPDATE_IS_PRIVATE,
  USERPROFILE_UPDATE_PROGRAM_PROFILE,
  USERPROFILES_SEARCH,
  USERS_SEARCH,
} from "Constants/ActionTypes";
import {
  handleCreateUser,
  handleCreateUserRole,
  handleDeleteUserRole,
  handleLoadUserRoles,
  handleSearchAccessUsers,
  handleUpdateUserRole,
  handleUpdateUserStatus,
} from "Dataflows/AccessControl/AccessControlSaga";
import { ACTIVITY_TYPE_EVENT_TYPE_DATA_LOAD } from "Dataflows/ActivityEventTypes/ActivityEventTypesActionTypes";
import { handleLoadActivityEventTypesResults } from "Dataflows/ActivityEventTypes/ActivityEventTypesSaga";
import {
  handleAssignApplication,
  handleDownloadApplicationsToCSV,
  handleFlagApplication,
  handleLoadApplication,
  handleLoadAssignApplication,
  handleLoadAvailableAssignApplication,
  handleBulkAssignApplication,
  handleScoreApplication,
  handleSearchApplications,
} from "Dataflows/Application/ApplicationSaga";
import {
  GET_ACTIVITY_EVENT_BY_ID,
  GET_ADDITIONAL_ACTIVITIES_EVENTS_BY_SELECTED_USERPROFILE_ID,
  GET_APPLICATION_FLAGS_BY_SELECTED_APPLICATION_ID,
  GET_APPLICATION_QUESTIONS_BY_SELECTED_APPLICATION_ID,
  GET_HIGHLIGHTED_ACTIVITIES_EVENTS_BY_SELECTED_APPLICATION_ID,
  GET_USER_PROFILE_BY_SELECTED_APPLICATION_ID,
  UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS,
  UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES,
} from "Dataflows/ApplicationDetails/ApplicationDetailsActionTypes";
import {
  handleActivityEventFetchById,
  handleAdditionalActivitiesEventsFetchByUserprofileId,
  handleApplicationQuestionsFetchByApplicationId,
  handleFetchApplicationFlagsByApplicationId,
  handleHighlightedActivitiesEventsFetchByApplicationId,
  handleUpdateApplicationStatusesAndRemoveFlags,
  handleUpdateApplicationTechnologyAreaAndResetScores,
  handleUserProfileFetchByApplicationId,
} from "Dataflows/ApplicationDetails/ApplicationDetailsSaga";
import { AWARD_PERIOD_LOAD } from "Dataflows/AwardPeriod/AwardPeriodActionTypes";
import { handleLoadAwardPeriods } from "Dataflows/AwardPeriod/AwardPeriodSaga";
import { AWARD_KIT_SKU_LOAD } from "Dataflows/AwardKitSku/AwardKitSkuActionTypes";
import { handleLoadAwardKitSku } from "Dataflows/AwardKitSku/AwardKitSkuSaga";
import {
  AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE,
  AZURE_BENEFIT_REQUESTS_SEARCH,
  GET_AZURE_BENEFIT_REQUEST_DETAILS,
} from "Dataflows/AzureBenefitRequests/AzureBenefitRequestsActionTypes";
import {
  handleApproveOrDenyAzureBenefitRequest,
  handleGetAzureBenefitRequestDetails,
  handleSearchAzureBenefitRequests,
} from "Dataflows/AzureBenefitRequests/AzureBenefitRequestsSaga";
import { BENEFITS_SEARCH } from "Dataflows/Benefit/BenefitActionTypes";
import { handleSearchBenefits } from "Dataflows/Benefit/BenefitSaga";
import { CL_REGION_LOAD, GEO_DATA_LOAD } from "Dataflows/ClRegion/ClRegionActionTypes";
import { handleLoadClRegions, handleLoadGeoData } from "Dataflows/ClRegion/ClRegionsSaga";
import { COMMON_DATA_LOAD } from "Dataflows/CommonData/CommonDataActionTypes";
import { handleLoadCommonData } from "Dataflows/CommonData/CommonDataSaga";
import { handleLoadCountryRegions } from "Dataflows/CountryRegion/CountryRegionSaga";
import { handleCapData, handleFilterDemographics } from "Dataflows/Demographics/DemographicsSaga";
import {
  ADD_MICROSOFT_MEMBER_TO_DL,
  APPROVE_OR_DECLINE_DISTRIBUTION_LIST_JOIN_REQUEST,
  DISTRIBUTION_LISTS_SEARCH,
  FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST,
  FETCH_MICROSOFT_MEMBERS_FOR_DL,
  REMOVE_MICROSOFT_MEMBER_FROM_DL,
} from "Dataflows/DistributionLists/DistributionListsActionTypes";
import {
  handleAddMicrosoftMemberToDL,
  handleApproveOrDeclineJoinRequest,
  handleFetchDistributionListByTenantRequest,
  handleFetchMicrosoftMembersForDL,
  handleRemoveMicrosoftMemberFromDL,
  handleSearchDistributionLists,
} from "Dataflows/DistributionLists/DistributionListsSaga";
import { INDUSTRY_FOCUS_LOAD } from "Dataflows/IndustryFocus/IndustryFocusActionTypes";
import { handleLoadIndustryFocuses } from "Dataflows/IndustryFocus/IndustryFocusSaga";
import { M365_COPILOT_BENEFITS_SEARCH } from "Dataflows/M365CopilotBenefit/M365CopilotBenefitActionType";
import { handleSearchM365CopilotBenefits } from "Dataflows/M365CopilotBenefit/M365CopilotBenefitSaga";
import {
  MVP_ADD_AZURE_BENEFIT_DETAIL,
  MVP_ADD_COMMENT,
  MVP_CLAIM_BENEFIT,
  MVP_CLAIM_BENEFIT_MANAGER,
  MVP_EXTEND_AZURE_BENEFIT_CREDIT,
  MVP_FETCH_COMMENTS,
  MVP_FETCH_PROFILE_BENEFITS,
  MVP_FETCH_PROGRAM_PROFILE,
  MVP_FETCH_RETIRE_PROFILE,
  MVP_FETCH_USER_PROFILE,
  MVP_GET_M365_BENEFIT_DETAIL,
  MVP_RETIRE_PROFILE,
  MVP_UPDATE_M365_BENEFIT_DETAIL,
  MVP_GET_MS365_BENEFIT_DETAIL,
  MVP_UPDATE_MS365_BENEFIT_DETAIL,
  MVP_UPDATE_PROGRAM_PROFILE,
  MVP_USER_PROFILES_SEARCH,
  MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION,
  MVP_USERPROFILE_UPDATE_BLOCK_SHIPPING,
  MVP_USERPROFILE_UPDATE_IS_PRIVATE,
  MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL,
} from "Dataflows/MVP/MVPActionTypes";
import {
  handleAddAzureBenefit,
  handleMVPAddComment,
  handleMVPClaimBenefitManager,
  handleMVPClaimBenefit,
  handleMVPCommentsFetch,
  handleMVPExtendAzureBenefitCredit,
  handleMVPFetchProfileBenefits,
  handleMVPGetM365BenefitDetail,
  handleMVPGetMS365BenefitDetail,
  handleMVPProgramProfileFetch,
  handleMVPRetireProfile,
  handleMVPRetireProfileFetch,
  handleMVPSearchUserProfiles,
  handleMVPUpdateM365BenefitDetail,
  handleMVPUpdateMS365BenefitDetail,
  handleMVPUpdateProgramProfile,
  handleMVPUpdateUserProfileBlockShipping,
  handleMVPUserProfileAnnualExceptionUpdate,
  handleMVPUserProfileFetch,
  handleMVPUserProfileIsPrivateUpdate,
  handleMVPUserProfilePrimaryEmailUpdate,
} from "Dataflows/MVP/MVPSaga";
import { handleLoadProgramLanguage } from "Dataflows/ProgramLanguage/ProgramLanguageSaga";
import {
  RD_ADD_AZURE_BENEFIT_DETAIL,
  RD_ADD_USER_PROFILE_COMMENT,
  RD_DOWNLOAD_TO_CSV,
  RD_EXTEND_AZURE_BENEFIT_CREDIT,
  RD_FETCH_PROGRAM_PROFILE,
  RD_FETCH_USER_PROFILE,
  RD_FETCH_USER_PROFILE_COMMENTS,
  RD_GET_M365_BENEFIT_DETAIL,
  RD_GET_PROFILE_BENEFITS,
  RD_UPDATE_M365_BENEFIT_DETAIL,
  RD_UPDATE_PROGRAM_PROFILE,
  RD_USER_PROFILE_CLAIM_BENEFIT,
  RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL,
  RD_USER_PROFILES_SEARCH,
  RD_USERPROFILE_UPDATE_IS_PRIVATE,
  RD_GET_MS365_BENEFIT_DETAIL,
  RD_UPDATE_MS365_BENEFIT_DETAIL,
} from "Dataflows/RD/UserProfile/RDUserProfileActionTypes";
import {
  handleRDAddUserProfileComment,
  handleRDClaimBenefit,
  handleRDExtendAzureBenefitCredit,
  handleRDGetM365BenefitDetail,
  handleRDGetMS365BenefitDetail,
  handleRDGetProfileBenefits,
  handleRDProgramProfileFetch,
  handleRDSearchUserProfiles,
  handleRDUpdateM365BenefitDetail,
  handleRDUpdateMS365BenefitDetail,
  handleRDUpdateProgramProfile,
  handleRDUserProfileCommentsFetch,
  handleRDUserProfileDownload,
  handleRDUserProfileFetch,
  handleRDUserProfileIsPrivateUpdate,
  handleRDUserProfilePrimaryEmailUpdate,
  handleRDAddAzureBenefit,
} from "Dataflows/RD/UserProfile/RDUserProfileSaga";
import { handleLoadRegions } from "Dataflows/Region/RegionSaga";
import { handleLoadSchools } from "Dataflows/School/SchoolSaga";
import { handleLoadSocialNetworks } from "Dataflows/SocialNetwork/SocialNetworkSaga";
import {
  handleLoadStateProvinces,
  handleLoadStateProvincesSchool,
  handleLoadStateProvincesShipping,
} from "Dataflows/StateProvince/StateProvinceSaga";
import { handleLoadTechnicalExpertise } from "Dataflows/TechnicalExpertise/TechnicalExpertiseSaga";
import { TECHNICAL_EXPERTISE_LOAD } from "Dataflows/TechnicalExpertise/TechnicalExpertiseTypes";
import { handleLoadTechAreaResults } from "Dataflows/TechnologyArea/TechnologyAreaSaga";
import { TECHAREADATA_LOAD } from "Dataflows/TechnologyArea/TechnologyAreaTypes";
import {
  handleAddComment,
  handleClaimBenefit,
  handleDlLoad,
  handleDlRemove,
  handleDlRemoveAll,
  handleEndUserProfileVSBenefits,
  handleExtendUserProfileVSBenefits,
  handleGetProfileBenefits,
  handleLoadUserProfile,
  handleLoadUserProfileByEmail,
  handleLoadUserProfileComments,
  handleLoadUserProfileVSBenefits,
  handleProgramProfileFetch,
  handleProvisionUserProfileVSBenefits,
  handleSearchUserProfiles,
  handleUpdateProgramProfile,
  handleUpdateUserProfile,
  handleUpdateUserProfileBlockShipping,
  handleUpdateUserProfileComment,
  handleUserProfileIsPrivateUpdate,
} from "Dataflows/UserProfile/UserProfileSaga";
import {
  DOWNLOAD_REQUESTS_TO_CSV,
  VISUAL_STUDIO_BENEFITS_SEARCH,
} from "Dataflows/VisualStudioBenefit/VisualStudioBenefitActionTypes";
import {
  handleDownloadRequestsToCSV,
  handleSearchVisualStudioBenefits,
} from "Dataflows/VisualStudioBenefit/VisualStudioBenefitSaga";

import { handleEventStatusUpdate, handleLoadEvent, handleSearchEvents } from "../Event/EventSaga";
import {
  SA_CSV_FILE_SUBMIT,
  SA_CSV_FILE_UPLOAD,
} from "Dataflows/SA/UpdateSARegistrationStatus/UpdateSARegistrationStatusActionType";
import {
  handleCsvFileSubmit,
  handleCsvFileUpload,
} from "Dataflows/SA/UpdateSARegistrationStatus/UpdateSARegistrationStatusSaga";
import {
  SA_PROGRAM_CSV_FILE_SUBMIT,
  SA_PROGRAM_CSV_FILE_UPLOAD,
} from "Dataflows/SA/UpdateSAProgramStatus/UpdateSAProgramStatusActionType";
import {
  handleProgramCsvFileSubmit,
  handleProgramCsvFileUpload,
} from "Dataflows/SA/UpdateSAProgramStatus/UpdateSAProgramStatusSaga";

/**
 * The root saga watches all saga activated actions by using "effects"
 * @generator
 * @returns {void} - TODO: figure out return type of generator functions.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* watchAll() {
  yield all([
    takeEvery(APPLICATION_LOAD, handleLoadApplication),
    takeEvery(GET_USER_PROFILE_BY_SELECTED_APPLICATION_ID, handleUserProfileFetchByApplicationId),
    takeEvery(
      GET_APPLICATION_FLAGS_BY_SELECTED_APPLICATION_ID,
      handleFetchApplicationFlagsByApplicationId
    ),
    takeEvery(
      GET_APPLICATION_QUESTIONS_BY_SELECTED_APPLICATION_ID,
      handleApplicationQuestionsFetchByApplicationId
    ),
    takeEvery(
      GET_HIGHLIGHTED_ACTIVITIES_EVENTS_BY_SELECTED_APPLICATION_ID,
      handleHighlightedActivitiesEventsFetchByApplicationId
    ),
    takeEvery(
      GET_ADDITIONAL_ACTIVITIES_EVENTS_BY_SELECTED_USERPROFILE_ID,
      handleAdditionalActivitiesEventsFetchByUserprofileId
    ),
    takeEvery(GET_ACTIVITY_EVENT_BY_ID, handleActivityEventFetchById),
    takeEvery(ACTIVITY_TYPE_EVENT_TYPE_DATA_LOAD, handleLoadActivityEventTypesResults),
    takeEvery(GET_ASSIGN_APPLICATION, handleLoadAssignApplication),
    takeEvery(GET_AVAILABLE_ASSIGN_APPLICATION, handleLoadAvailableAssignApplication),
    takeEvery(ASSIGN_APPLICATION, handleAssignApplication),
    takeEvery(BULK_ASSIGN_APPLICATION, handleBulkAssignApplication),
    takeEvery(SCORE_APPLICATION, handleScoreApplication),
    takeEvery(FLAG_APPLICATION, handleFlagApplication),
    takeEvery(APPLICATIONS_SEARCH, handleSearchApplications),
    takeEvery(AZURE_BENEFIT_REQUESTS_SEARCH, handleSearchAzureBenefitRequests),
    takeEvery(AZURE_BENEFIT_REQUEST_APPROVE_OR_DECLINE, handleApproveOrDenyAzureBenefitRequest),
    takeEvery(GET_AZURE_BENEFIT_REQUEST_DETAILS, handleGetAzureBenefitRequestDetails),
    takeEvery(BENEFITS_SEARCH, handleSearchBenefits),
    takeEvery(VISUAL_STUDIO_BENEFITS_SEARCH, handleSearchVisualStudioBenefits),
    takeEvery(DOWNLOAD_REQUESTS_TO_CSV, handleDownloadRequestsToCSV),
    takeEvery(AWARD_PERIOD_LOAD, handleLoadAwardPeriods),
    takeEvery(AWARD_KIT_SKU_LOAD, handleLoadAwardKitSku),
    takeEvery(CL_REGION_LOAD, handleLoadClRegions),
    takeEvery(REGIONDATA_LOAD, handleLoadRegions),
    takeEvery(COUNTRYDATA_LOAD, handleLoadCountryRegions),
    takeEvery(CREATE_USER, handleCreateUser),
    takeEvery(DOWNLOAD_APPLICATIONS_TO_CSV, handleDownloadApplicationsToCSV),
    takeEvery(EVENT_LOAD, handleLoadEvent),
    takeEvery(EVENTS_SEARCH, handleSearchEvents),
    takeEvery(INDUSTRY_FOCUS_LOAD, handleLoadIndustryFocuses),
    takeEvery(MVP_FETCH_PROFILE_BENEFITS, handleMVPFetchProfileBenefits),
    takeEvery(MVP_CLAIM_BENEFIT_MANAGER, handleMVPClaimBenefitManager),
    takeEvery(MVP_CLAIM_BENEFIT, handleMVPClaimBenefit),
    takeEvery(MVP_ADD_COMMENT, handleMVPAddComment),
    takeEvery(MVP_FETCH_COMMENTS, handleMVPCommentsFetch),
    takeEvery(MVP_EXTEND_AZURE_BENEFIT_CREDIT, handleMVPExtendAzureBenefitCredit),
    takeEvery(RD_EXTEND_AZURE_BENEFIT_CREDIT, handleRDExtendAzureBenefitCredit),
    takeEvery(MVP_ADD_AZURE_BENEFIT_DETAIL, handleAddAzureBenefit),
    takeEvery(MVP_FETCH_PROGRAM_PROFILE, handleMVPProgramProfileFetch),
    takeEvery(MVP_FETCH_RETIRE_PROFILE, handleMVPRetireProfileFetch),
    takeEvery(MVP_UPDATE_PROGRAM_PROFILE, handleMVPUpdateProgramProfile),
    takeEvery(MVP_RETIRE_PROFILE, handleMVPRetireProfile),
    takeEvery(MVP_FETCH_USER_PROFILE, handleMVPUserProfileFetch),
    takeEvery(MVP_GET_M365_BENEFIT_DETAIL, handleMVPGetM365BenefitDetail),
    takeEvery(MVP_UPDATE_M365_BENEFIT_DETAIL, handleMVPUpdateM365BenefitDetail),
    takeEvery(RD_GET_M365_BENEFIT_DETAIL, handleRDGetM365BenefitDetail),
    takeEvery(RD_UPDATE_M365_BENEFIT_DETAIL, handleRDUpdateM365BenefitDetail),
    takeEvery(MVP_GET_MS365_BENEFIT_DETAIL, handleMVPGetMS365BenefitDetail),
    takeEvery(MVP_UPDATE_MS365_BENEFIT_DETAIL, handleMVPUpdateMS365BenefitDetail),
    takeEvery(RD_GET_MS365_BENEFIT_DETAIL, handleRDGetMS365BenefitDetail),
    takeEvery(RD_UPDATE_MS365_BENEFIT_DETAIL, handleRDUpdateMS365BenefitDetail),
    takeEvery(MVP_USER_PROFILES_SEARCH, handleMVPSearchUserProfiles),
    takeEvery(RD_ADD_USER_PROFILE_COMMENT, handleRDAddUserProfileComment),
    takeEvery(RD_DOWNLOAD_TO_CSV, handleRDUserProfileDownload),
    takeEvery(RD_FETCH_USER_PROFILE_COMMENTS, handleRDUserProfileCommentsFetch),
    takeEvery(RD_FETCH_USER_PROFILE, handleRDUserProfileFetch),
    takeEvery(RD_FETCH_PROGRAM_PROFILE, handleRDProgramProfileFetch),
    takeEvery(RD_GET_PROFILE_BENEFITS, handleRDGetProfileBenefits),
    takeEvery(RD_UPDATE_PROGRAM_PROFILE, handleRDUpdateProgramProfile),
    takeEvery(RD_USER_PROFILE_CLAIM_BENEFIT, handleRDClaimBenefit),
    takeEvery(RD_USER_PROFILES_SEARCH, handleRDSearchUserProfiles),
    takeEvery(SCHOOLDATA_LOAD, handleLoadSchools),
    takeEvery(SOCIALNETWORKDATA_LOAD, handleLoadSocialNetworks),
    takeEvery(PROGRAM_LANGUAGE_LOAD, handleLoadProgramLanguage),
    takeEvery(STATEPROVINCEDATA_LOAD, handleLoadStateProvinces),
    takeEvery(STATEPROVINCESCHOOL_LOAD, handleLoadStateProvincesSchool),
    takeEvery(STATEPROVINCESHIPPING_LOAD, handleLoadStateProvincesShipping),
    takeEvery(TECHAREADATA_LOAD, handleLoadTechAreaResults),
    takeEvery(TECHNICAL_EXPERTISE_LOAD, handleLoadTechnicalExpertise),
    takeEvery(UPDATE_EVENT_STATUS, handleEventStatusUpdate),
    takeEvery(UPDATE_USER_STATUS, handleUpdateUserStatus),
    takeEvery(
      UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS,
      handleUpdateApplicationStatusesAndRemoveFlags
    ),
    takeEvery(
      UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES,
      handleUpdateApplicationTechnologyAreaAndResetScores
    ),
    takeEvery(USER_ROLES_LOAD, handleLoadUserRoles),
    takeEvery(USER_ROLE_DELETE, handleDeleteUserRole),
    takeEvery(USER_ROLE_CREATE, handleCreateUserRole),
    takeEvery(GEO_DATA_LOAD, handleLoadGeoData),
    takeEvery(USER_ROLE_UPDATE, handleUpdateUserRole),
    takeEvery(USERPROFILE_CLAIM_BENEFIT, handleClaimBenefit),
    takeEvery(USERPROFILE_END_VSBENEFITS, handleEndUserProfileVSBenefits),
    takeEvery(USERPROFILE_EXTEND_VSBENEFITS, handleExtendUserProfileVSBenefits),
    takeEvery(USERPROFILE_FETCH_PROGRAM_PROFILE, handleProgramProfileFetch),
    takeEvery(USERPROFILE_GET_PROFILE_BENEFITS, handleGetProfileBenefits),
    takeEvery(USERPROFILE_LOAD_BY_EMAIL, handleLoadUserProfileByEmail),
    takeEvery(USERPROFILE_LOAD_COMMENTS, handleLoadUserProfileComments),
    takeEvery(USERPROFILE_LOAD_VSBENEFITS, handleLoadUserProfileVSBenefits),
    takeEvery(USERPROFILE_LOAD, handleLoadUserProfile),
    takeEvery(USERPROFILE_PROVISION_VSBENEFITS, handleProvisionUserProfileVSBenefits),
    takeEvery(USERPROFILE_UPDATE_COMMENT, handleUpdateUserProfileComment),
    takeEvery(USERPROFILE_UPDATE_PROGRAM_PROFILE, handleUpdateProgramProfile),
    takeEvery(USERPROFILE_UPDATE, handleUpdateUserProfile),
    takeEvery(USERPROFILE_UPDATE_BLOCK_SHIPPING, handleUpdateUserProfileBlockShipping),
    takeEvery(MVP_USERPROFILE_UPDATE_BLOCK_SHIPPING, handleMVPUpdateUserProfileBlockShipping),
    takeEvery(USERPROFILES_SEARCH, handleSearchUserProfiles),
    takeEvery(USERPROFILE_ADD_COMMENT, handleAddComment),
    takeEvery(USERS_SEARCH, handleSearchAccessUsers),
    takeEvery(USERPROFILE_DL_LOAD, handleDlLoad),
    takeEvery(USERPROFILE_DL_REMOVE, handleDlRemove),
    takeEvery(USERPROFILE_DL_REMOVE_ALL, handleDlRemoveAll),
    takeEvery(DISTRIBUTION_LISTS_SEARCH, handleSearchDistributionLists),
    takeEvery(
      FETCH_DISTRIBUTION_LISTS_BY_TENANT_REQUEST,
      handleFetchDistributionListByTenantRequest
    ),
    takeEvery(APPROVE_OR_DECLINE_DISTRIBUTION_LIST_JOIN_REQUEST, handleApproveOrDeclineJoinRequest),
    takeEvery(FETCH_MICROSOFT_MEMBERS_FOR_DL, handleFetchMicrosoftMembersForDL),
    takeEvery(ADD_MICROSOFT_MEMBER_TO_DL, handleAddMicrosoftMemberToDL),
    takeEvery(REMOVE_MICROSOFT_MEMBER_FROM_DL, handleRemoveMicrosoftMemberFromDL),
    takeEvery(USERPROFILE_UPDATE_IS_PRIVATE, handleUserProfileIsPrivateUpdate),
    takeEvery(MVP_USERPROFILE_UPDATE_IS_PRIVATE, handleMVPUserProfileIsPrivateUpdate),
    takeEvery(MVP_USERPROFILE_UPDATE_ANNUAL_EXCEPTION, handleMVPUserProfileAnnualExceptionUpdate),
    takeEvery(RD_USERPROFILE_UPDATE_IS_PRIVATE, handleRDUserProfileIsPrivateUpdate),
    takeEvery(DEMOGRAPHICS_FILTER, handleFilterDemographics),
    takeEvery(CAPACITY_DATA_LOAD, handleCapData),
    takeEvery(COMMON_DATA_LOAD, handleLoadCommonData),
    takeEvery(MVP_USERPROFILE_UPDATE_PRIMARY_EMAIL, handleMVPUserProfilePrimaryEmailUpdate),
    takeEvery(RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL, handleRDUserProfilePrimaryEmailUpdate),
    takeEvery(RD_ADD_AZURE_BENEFIT_DETAIL, handleRDAddAzureBenefit),
    takeEvery(M365_COPILOT_BENEFITS_SEARCH, handleSearchM365CopilotBenefits),
    takeEvery(SA_CSV_FILE_UPLOAD, handleCsvFileUpload),
    takeEvery(SA_CSV_FILE_SUBMIT, handleCsvFileSubmit),
    takeEvery(SA_PROGRAM_CSV_FILE_UPLOAD, handleProgramCsvFileUpload),
    takeEvery(SA_PROGRAM_CSV_FILE_SUBMIT, handleProgramCsvFileSubmit),
  ]);
}

export default watchAll;
